import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import { Controller, useForm } from 'react-hook-form';
import {
  GuestVisitInvestor,
  UpdateGuestVisitForm,
} from './models/guestInvestorModel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import LoadingCard from '../../components/LoadingCard';
import {
  getGuestVisitInvestorDetailEndpoint,
  updateGuestVisitEndpoint,
} from './services/guestInvestorService';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import AdminPage from '../../components/admin/AdminPage';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Row,
  UncontrolledAlert,
} from 'reactstrap';
import Select from 'react-select';
import { listToSelectOptionAdapter } from '../../adapters/listAdapter';
import { getAvailableTimeProjectVisitsEndpoint } from './services/visitsInvestorService';
import { UserType } from '../../constants/profileConstants';

const InvestGuestVisitDetailEdit = () => {
  const pageName = 'Detalle de Asesoría';
  const pageParentName = 'Asesorías';
  const { id: guestVisitId = '' } = useParams();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [guestVisitInvestor, setGuestVisitInvestor] =
    useState<GuestVisitInvestor>();

  const [error, setError] = useState('');
  const [availableTimesForVisit, setAvailableTimesForVisit] = useState([]);

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<UpdateGuestVisitForm>({
    resolver: yupResolver(
      yup.object({
        date: yup.date().required('La fecha de la visita es requerida.'),
        start_time: yup
          .number()
          .integer()
          .positive()
          .required('La hora de la visita es requerida.'),
      }),
    ),
  });

  const loadGuestVisitInvestors = async () => {
    const { status, data } = await callEndpoint(
      getGuestVisitInvestorDetailEndpoint(guestVisitId, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200) {
      setGuestVisitInvestor(response.data);
    } else {
      setErrorAlert(response.message);
    }
  };
  useEffect(() => {
    loadGuestVisitInvestors().catch((e) => setErrorAlert(e.toString()));
  }, []);

  const doUpdate = useCallback(
    async (form: UpdateGuestVisitForm) => {
      if (!guestVisitInvestor?.visitInfo?.id) return;

      const { status, data } = await callEndpoint(
        updateGuestVisitEndpoint(guestVisitInvestor.visitInfo.id, form, token),
      );
      const response = fetchResponseAdapter(data);
      if (status === 200) {
        navigate(`/investors/guest/visits/${guestVisitInvestor.visitInfo.id}`);
      } else {
        setError(response.message);
      }
    },
    [guestVisitInvestor],
  );

  const loadAvailableTimesForVisit = useCallback(
    async (dateForSearch: string) => {
      if (!guestVisitInvestor?.projectInfo) return;

      const { status, data } = await callEndpoint(
        getAvailableTimeProjectVisitsEndpoint(
          guestVisitInvestor?.projectInfo.id,
          dateForSearch,
          token,
        ),
      );
      const response = fetchResponseAdapter(data);
      if (status === 200) {
        setAvailableTimesForVisit(response.data);
      } else {
        setError(response.message);
      }
    },
    [guestVisitInvestor, callEndpoint, token],
  );

  if (!guestVisitInvestor || isLoading)
    return <LoadingCard name={pageName} parent_name={pageParentName} />;

  return (
    <AdminPage name={pageName} parentName={pageParentName}>
      <h3 className="mb-4">
        Editar asesoria de{' '}
        {guestVisitInvestor.userInfo.type === UserType.LANDLORD
          ? 'Inversionista'
          : 'Guest'}
      </h3>
      <Form role="form" onSubmit={handleSubmit(doUpdate)}>
        <Col md="6">
          <Row>
            <Col md="12">
              {error && (
                <UncontrolledAlert color="danger" fade={false}>
                  <span className="alert-inner--text">
                    <strong> Ops! </strong>
                    {error}
                  </span>
                </UncontrolledAlert>
              )}
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="estimate_moving_at"
                >
                  Fecha de visita (Actual:{' '}
                  {`${guestVisitInvestor.visitInfo.visit_date} ${guestVisitInvestor.visitInfo.visit_time}`}
                  )
                </label>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <Input
                      className={errors.date?.message && 'is-invalid'}
                      {...field}
                      id="date"
                      type="date"
                      onChange={(e) => {
                        const currentDate = e.target.value;
                        setValue('date', currentDate);
                        loadAvailableTimesForVisit(currentDate).catch((e) =>
                          setError(e.toString()),
                        );
                      }}
                    />
                  )}
                />
                <h5 className="text-danger">{errors.date?.message}</h5>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <FormGroup>
                <label className="form-control-label" htmlFor="period">
                  Hora de la visita
                </label>
                <Controller
                  name="start_time"
                  control={control}
                  render={() => (
                    <Select
                      placeholder="Selecciona el tiempo de la visita"
                      options={
                        listToSelectOptionAdapter(
                          availableTimesForVisit,
                          'minutes',
                          'time_value',
                        ) as Array<any>
                      }
                      onChange={(optionSelected) =>
                        setValue('start_time', optionSelected?.value)
                      }
                    />
                  )}
                />
                <h5 className="text-danger">{errors.start_time?.message}</h5>
              </FormGroup>
            </Col>
          </Row>
        </Col>
        <Button color={'primary'} disabled={isLoading}>
          Actualizar
        </Button>
      </Form>
    </AdminPage>
  );
};

export default InvestGuestVisitDetailEdit;
